import { useState, useEffect, useCallback } from "react";
import {
  Drawer, Button, Space, Spin, Alert, Typography, Tag, Descriptions, Tooltip,
} from "antd";
import { SaveOutlined, ReloadOutlined, TagsOutlined } from "@ant-design/icons";
import axios from "axios";
import API_URLS from "../../../config/global_variables";
import { triggerNotification } from "../../common/notify";
import TagSelector from "../../common/TagSelector";

const authToken = () => {
  try { return JSON.parse(localStorage.userData || "{}").token || ""; } catch { return ""; }
};
const headers = () => ({ "Content-Type": "application/json", Authorization: authToken() });

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  return apiErr?.message || err?.message || "Something went wrong";
};

const bulkUpsert = async (variantId, storeHash, storeLabel, items) => {
  const res = await axios.post(
    API_URLS.PRODUCT_KP_BULK(variantId),
    { store_hash: storeHash, store_label: storeLabel || "", items },
    { headers: headers() }
  );
  return res?.data?.data?.items || [];
};

const listKp = async (variantId, storeHash) => {
  const res = await axios.get(API_URLS.PRODUCT_KP(variantId), {
    headers: headers(),
    params: { store_hash: storeHash },
  });
  return res?.data?.data?.records || [];
};

const tagsKey = (tags) => tags.map((t) => `${t.id}:${t.points}`).join(",");

const ProductKeywordDrawer = ({ open, product, storeHash, storeLabel, onClose, onCountChange }) => {
  const variantId = product?.raw?.variant_id;

  const [selectedTags, setSelectedTags] = useState([]);
  const [savedKey,     setSavedKey]     = useState("");
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [loadError,    setLoadError]    = useState(null);

  const isDirty = tagsKey(selectedTags) !== savedKey;

  useEffect(() => {
    if (variantId) onCountChange?.(variantId, selectedTags.length);
  }, [variantId, selectedTags.length, onCountChange]);

  const fetchList = useCallback(async () => {
    if (!variantId || !storeHash) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listKp(variantId, storeHash);
      const tags = rows.map((r) => ({
        id: r.tagId ?? r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
      }));
      setSelectedTags(tags);
      setSavedKey(tagsKey(tags));
    } catch (err) {
      setLoadError(mapError(err));
    } finally {
      setLoading(false);
    }
  }, [variantId, storeHash]);

  useEffect(() => {
    if (open) fetchList();
    else { setSelectedTags([]); setSavedKey(""); }
  }, [open, fetchList]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const items = selectedTags.map((t) => ({
        tag_id: Number(t.id),
        points: Number(t.points) || 0,
      }));
      const updated = await bulkUpsert(variantId, storeHash, storeLabel, items);
      const savedTags = updated.map((r) => ({
        id: r.tagId ?? r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
      }));
      setSelectedTags(savedTags);
      setSavedKey(tagsKey(savedTags));
      triggerNotification({
        type: "success",
        title: `${items.length} tag${items.length === 1 ? "" : "s"} saved`,
      });
    } catch (err) {
      triggerNotification({ type: "error", title: "Save failed", message: mapError(err) });
    } finally {
      setSaving(false);
    }
  };

  const isReady = !!variantId && !!storeHash;

  return (
    <Drawer
      title={
        <Space>
          <TagsOutlined />
          <span>Product Tags and Points</span>
        </Space>
      }
      width={580}
      open={open}
      onClose={onClose}
      destroyOnClose
      maskClosable={false}
      extra={
        <Space>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={fetchList} loading={loading} disabled={saving} />
          </Tooltip>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!isReady || loading || !isDirty}
          >
            Save tags
          </Button>
        </Space>
      }
    >
      {product?.sapName && (
        <Typography.Text strong style={{ fontSize: 14, display: "block", marginBottom: 12 }}>
          {product.sapName}
        </Typography.Text>
      )}

      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Variant ID">
          <Typography.Text code>{variantId ?? "—"}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="BC SKU">{product?.bcSku || "—"}</Descriptions.Item>
        <Descriptions.Item label="Store">
          <Tag color="geekblue">{storeLabel || "—"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Store Hash">
          <Typography.Text code style={{ fontSize: 11 }}>{storeHash || "—"}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      {!isReady && (
        <Alert
          type="warning"
          showIcon
          message="Missing variant ID or store hash — cannot manage keyword points."
          style={{ marginBottom: 16 }}
        />
      )}

      {loadError && (
        <Alert
          type="error"
          showIcon
          closable
          message={loadError}
          onClose={() => setLoadError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
      ) : (
        <>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            Select tags from the global tags library. Drag to reorder. Set points per tag.
          </Typography.Text>
          <TagSelector
            value={selectedTags}
            onChange={setSelectedTags}
            withPoints
            disabled={!isReady}
            placeholder="Search and select tags…"
          />
          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {selectedTags.length} tag{selectedTags.length === 1 ? "" : "s"} selected
            </Typography.Text>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default ProductKeywordDrawer;
